import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageWrapper } from '../components/layout/PageWrapper';
import { OrnamentDivider } from '../components/ui/OrnamentDivider';
import { useSeasons, useSeason, useRenamePlayer } from '../hooks/useScoreboard';
import { useAuthStore } from '../store/authStore';
import { isFreeAdmin } from '../lib/freeAdmins';
import type { PlayerScore } from '../types/scoreboard';

function PlayerRow({ seasonId, player }: { seasonId: string; player: PlayerScore }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(player.currentDisplayName ?? player.displayName);
  const [error, setError] = useState('');
  const renamePlayer = useRenamePlayer();

  async function handleSave() {
    setError('');
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Имя не может быть пустым.');
      return;
    }
    try {
      await renamePlayer.mutateAsync({ seasonId, playerKey: player.userId, newName: trimmed });
      setEditing(false);
    } catch (err: unknown) {
      setError((err as Error).message || 'Не удалось переименовать игрока.');
    }
  }

  return (
    <div className="flex items-center gap-3 py-3 px-4 border-b border-mafia-border last:border-b-0">
      <div className="flex-1">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              className="flex-1 bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 focus:outline-none focus:border-mafia-gold transition-colors"
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={renamePlayer.isPending}
              className="btn-gold-solid px-3 py-2 text-xs disabled:opacity-50"
            >
              {renamePlayer.isPending ? '...' : 'Сохранить'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setName(player.currentDisplayName ?? player.displayName);
                setError('');
              }}
              className="px-3 py-2 text-xs font-mono border border-mafia-border text-mafia-paper-dim hover:text-mafia-paper transition-colors"
            >
              Отмена
            </button>
          </div>
        ) : (
          <p className="text-mafia-paper font-medium">
            {player.currentDisplayName ?? player.displayName}
          </p>
        )}
        {error && <p className="text-mafia-crimson-light text-xs font-mono mt-1">{error}</p>}
      </div>
      <p className="text-mafia-muted text-xs font-mono w-24 text-right">
        {player.points.toFixed(1)} очк.
      </p>
      {!editing && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-mafia-muted hover:text-mafia-gold text-xs font-mono underline underline-offset-2 transition-colors"
        >
          Переименовать
        </button>
      )}
    </div>
  );
}

export function AdminPlayersPage() {
  const { user } = useAuthStore();
  const { data: seasons, isLoading: seasonsLoading } = useSeasons();
  const currentSeason = seasons?.find((s) => s.isCurrent) ?? seasons?.[0];
  const { data: seasonData, isLoading: scoresLoading } = useSeason(currentSeason?.seasonId ?? '');

  if (!isFreeAdmin(user?.email)) {
    return (
      <PageWrapper>
        <p className="text-mafia-paper-dim">Нет доступа.</p>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper className="max-w-2xl">
      <Link to="/scoreboard" className="font-mono text-xs text-mafia-muted hover:text-mafia-gold tracking-wider uppercase transition-colors mb-8 inline-block">
        ← Турнирная таблица
      </Link>

      <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase mb-3">◆ Админ ◆</p>
      <h1 className="font-display text-4xl font-bold text-mafia-paper mb-2">Игроки</h1>
      {currentSeason && (
        <p className="font-mono text-xs text-mafia-muted tracking-wider uppercase mb-8">
          Сезон: {currentSeason.name}
        </p>
      )}

      <OrnamentDivider className="mb-8" />

      {seasonsLoading || scoresLoading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-mafia-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !seasonData || seasonData.scores.length === 0 ? (
        <p className="text-mafia-paper-dim font-mono text-sm">В этом сезоне пока нет игроков.</p>
      ) : (
        <div className="card-vintage">
          {seasonData.scores.map((player) => (
            <PlayerRow key={player.userId} seasonId={currentSeason!.seasonId} player={player} />
          ))}
        </div>
      )}
    </PageWrapper>
  );
}
